import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import Practice from "api/types/models/practice";
import PracticeColours from "api/types/models/practice_colours";
import PracticeLink from "api/types/models/practice_link";
import PracticeTheme from "api/types/models/practice_theme";

import DefaultLoginImages from "assets/images/login-images";

export interface DefaultLoginImage {
  id: string;
  url: string;
}

export interface PracticeState {
  id?: number;
  name?: string;
  email?: string;
  phone?: string;
  displayName?: string;
  websiteLink?: string;
  privacyLink?: string;
  address?: string;
  theme: ThemeState;
  links: PracticeLink[];
}

export interface ThemeState {
  darkColor: string | null;
  onDarkColor: string | null;
  primaryColor: string | null;
  onPrimaryColor: string | null;
  lightColor: string | null;
  onLightColor: string | null;
  lightestColor: string | null;
  onLightestColor: string | null;
  logoUrl: string | null;
  logoFileId: number | null;
  defaultLoginImage: string | null;
  customLoginImage: string | null;
  customLoginImageFileId: number | null;
  defaultLoginImages: DefaultLoginImage[];
}

const initialState: PracticeState = {
  id: undefined,
  name: undefined,
  email: undefined,
  phone: undefined,
  displayName: undefined,
  websiteLink: undefined,
  privacyLink: undefined,
  address: undefined,
  links: [],
  theme: {
    darkColor: null,
    onDarkColor: null,
    primaryColor: null,
    onPrimaryColor: null,
    lightColor: null,
    onLightColor: null,
    lightestColor: null,
    onLightestColor: null,
    logoUrl: null,
    logoFileId: null,
    defaultLoginImage: null,
    customLoginImage: null,
    customLoginImageFileId: null,
    defaultLoginImages: [
      ...Object.values(DefaultLoginImages).map((image, idx) => ({
        id: `default-${idx + 1}`,
        url: image,
      })),
    ],
  },
};

export const practiceSlice = createSlice({
  name: "practice",
  initialState,
  reducers: {
    setColours: (state, colours: PayloadAction<Partial<PracticeColours>>) => {
      if (colours.payload?.dark_color) {
        state.theme.darkColor = colours.payload.dark_color;
      }

      if (colours.payload?.on_dark_color) {
        state.theme.onDarkColor = colours.payload.on_dark_color;
      }

      if (colours.payload?.primary_color) {
        state.theme.primaryColor = colours.payload.primary_color;
      }

      if (colours.payload?.on_primary_color) {
        state.theme.onPrimaryColor = colours.payload.on_primary_color;
      }

      if (colours.payload?.light_color) {
        state.theme.lightColor = colours.payload.light_color;
      }

      if (colours.payload?.on_light_color) {
        state.theme.onLightColor = colours.payload.on_light_color;
      }

      if (colours.payload?.lightest_color) {
        state.theme.lightestColor = colours.payload.lightest_color;
      }

      if (colours.payload?.on_lightest_color) {
        state.theme.onLightestColor = colours.payload.on_lightest_color;
      }
    },

    setTheme: (state, theme: PayloadAction<PracticeTheme>) => {
      state.theme.darkColor = theme.payload.dark_color;
      state.theme.onDarkColor = theme.payload.on_dark_color;
      state.theme.primaryColor = theme.payload.primary_color;
      state.theme.onPrimaryColor = theme.payload.on_primary_color;
      state.theme.lightColor = theme.payload.light_color;
      state.theme.onLightColor = theme.payload.on_light_color;
      state.theme.lightestColor = theme.payload.lightest_color;
      state.theme.onLightestColor = theme.payload.on_lightest_color;
      state.theme.logoUrl = theme.payload.logo_url;
      state.theme.logoFileId = theme.payload.logo_file_id;
      state.theme.defaultLoginImage = theme.payload.default_login_image;
      state.theme.customLoginImage = theme.payload.custom_login_image;
      state.theme.customLoginImageFileId =
        theme.payload.custom_login_image_file_id;
    },

    setPractice: (state, practice: PayloadAction<Practice>) => {
      state.id = practice.payload.id;
      state.name = practice.payload.name;
      state.email = practice.payload.email;
      state.phone = practice.payload.phone;
      state.displayName = practice.payload.display_name;
      state.websiteLink = practice.payload.website_link;
      state.privacyLink = practice.payload.privacy_link;
      state.address = practice.payload.address;
      state.links = practice.payload.links;

      state.theme.darkColor = practice.payload.theme?.dark_color;
      state.theme.onDarkColor = practice.payload.theme?.on_dark_color;
      state.theme.primaryColor = practice.payload.theme?.primary_color;
      state.theme.onPrimaryColor = practice.payload.theme?.on_primary_color;
      state.theme.lightColor = practice.payload.theme?.light_color;
      state.theme.onLightColor = practice.payload.theme?.on_light_color;
      state.theme.lightestColor = practice.payload.theme?.lightest_color;
      state.theme.onLightestColor = practice.payload.theme?.on_lightest_color;
      state.theme.logoUrl = practice.payload.theme?.logo_url;
      state.theme.logoFileId = practice.payload.theme?.logo_file_id;
      state.theme.defaultLoginImage =
        practice.payload.theme?.default_login_image;
      state.theme.customLoginImage = practice.payload.theme?.custom_login_image;
      state.theme.customLoginImageFileId =
        practice.payload.theme?.custom_login_image_file_id;

      if (state.theme.primaryColor) {
        document
          .querySelector('meta[name="theme-color"]')
          ?.setAttribute("content", `rgb(${state.theme.primaryColor})`);
      }

      if (state.theme.logoUrl) {
        document
          .querySelector('link[rel="icon"]')
          ?.setAttribute("href", state.theme.logoUrl);

        document
          .querySelector('link[rel="apple-touch-icon"]')
          ?.setAttribute("href", state.theme.logoUrl);
      }
    },

    clearPractice: (state) => {
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", "rgb(60 40 155)");

      document
        .querySelector('link[rel="icon"]')
        ?.setAttribute("href", "/logo.png");

      document
        .querySelector('link[rel="apple-touch-icon"]')
        ?.setAttribute("href", "/logo.png");

      return initialState;
    },
  },
});

export const { setPractice, setTheme, setColours, clearPractice } =
  practiceSlice.actions;

export default practiceSlice.reducer;
